'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
}

interface AuthContextType {
  user: UserProfile | null;
  session: any;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  createUser: (params: {
    email: string;
    password: string;
    role: string;
    displayName: string;
  }) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

async function fetchProfile(userId: string): Promise<UserProfile | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, email, full_name, role, is_active')
    .eq('id', userId)
    .single();
  if (error || !data) return null;
  return {
    id: data.id,
    email: data.email,
    displayName: data.full_name || data.email,
    role: data.role,
    isActive: data.is_active,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    let isMounted = true;
    const fallbackTimeout = window.setTimeout(() => {
      if (isMounted) {
        console.warn(
          '[AuthContext] Session verification timeout reached, falling back to unauthenticated state.'
        );
        setLoading(false);
      }
    }, 7000);

    const initializeAuth = async () => {
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve({ data: { session: null } }), 5000)
        );
        const result = (await Promise.race([sessionPromise, timeoutPromise])) as {
          data: { session: any };
        };

        if (isMounted) {
          setSession(result.data.session);
          if (result.data.session?.user) {
            const profile = await fetchProfile(result.data.session.user.id);
            if (isMounted) {
              setUser(profile);
            }
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        if (isMounted) {
          setLoading(false);
        }
      } finally {
        window.clearTimeout(fallbackTimeout);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (isMounted) {
        setSession(session);
        if (session?.user) {
          const profile = await fetchProfile(session.user.id);
          if (isMounted) {
            setUser(profile);
          }
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      window.clearTimeout(fallbackTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await Promise.race([
        supabase.auth.signInWithPassword({ email, password }),
        new Promise<any>((_, reject) => setTimeout(() => reject(new Error('Login timeout')), 5000)),
      ]);

      if (error) {
        return {
          success: false,
          error: 'Invalid credentials — use the demo accounts below to sign in',
        };
      }
      if (data.user) {
        const profile = await fetchProfile(data.user.id);
        setUser(profile);
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: 'Login failed — use the demo accounts below to sign in',
      };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const createUser = async (params: {
    email: string;
    password: string;
    role: string;
    displayName: string;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const result = await response.json();
      return response.ok ? { success: true } : { success: false, error: result.error };
    } catch {
      return { success: false, error: 'Could not create user' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
        createUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
